import { type RefObject, useEffect } from 'react';

import { IS_PREVIEW_ID } from 'components/mrp/components/AppointmentForm/AppointmentForm.consts';
import type { CalendarAppointmentEvent } from 'components/mrp/components/Calendar/Calendar.types';
import type { CalendarPosition } from '../CalendarNavigationHandler.types';

type UseEventSelectionSyncParams = {
  calendarEvents: { [calendarId: string]: CalendarAppointmentEvent[] };
  event: CalendarAppointmentEvent | undefined;
  position: CalendarPosition | undefined;
  setEvent: (event?: CalendarAppointmentEvent) => void;
  pendingRestoreEventId: RefObject<string | number | undefined>;
  pendingRestoreOriginalAppointmentId: RefObject<string | number | undefined>;
  clearPendingRestore: () => void;
};

export const useEventSelectionSync = ({
  calendarEvents,
  event,
  position,
  setEvent,
  pendingRestoreEventId,
  pendingRestoreOriginalAppointmentId,
  clearPendingRestore
}: UseEventSelectionSyncParams) => {
  'use memo';
  useEffect(() => {
    if (!position) return;

    const currentCalendarEvents =
      calendarEvents[position.calendarId || -1] || [];
    const hasPreviewEvent = currentCalendarEvents.some(
      (e) => e.id === IS_PREVIEW_ID
    );

    if (event) {
      // Deselect event when no longer available in the active position.
      if (currentCalendarEvents.indexOf(event) === -1) {
        // After a refetch, event object references change — match by id to preserve selection.
        const matchedEvent = currentCalendarEvents.find(
          (e) =>
            e.id === event.id ||
            e.planningEvent?.originalAppointmentId ===
              event.planningEvent?.originalAppointmentId
        );
        if (!hasPreviewEvent) {
          setEvent(matchedEvent);
        }
      }
    } else if (
      pendingRestoreEventId.current !== undefined &&
      !hasPreviewEvent
    ) {
      // Form just closed — restore the previously selected event by id.
      const idToRestore = pendingRestoreEventId.current;
      const matchedEvent = currentCalendarEvents.find(
        (e) =>
          String(e.id) === String(idToRestore) ||
          String(e.planningEvent?.originalAppointmentId) ===
            String(pendingRestoreOriginalAppointmentId.current)
      );
      if (matchedEvent) {
        clearPendingRestore();
        setEvent(matchedEvent);
      }
    }
  }, [
    calendarEvents,
    clearPendingRestore,
    event,
    pendingRestoreEventId,
    pendingRestoreOriginalAppointmentId,
    position,
    setEvent
  ]);
};
